import {
  EventCategory,
  EventMetadata,
  EventPublishingOptions,
  EventSource,
  EventPriority,
  RuntimeEvent,
  RuntimeEventType
} from './contracts'
import { RuntimeEventBus } from './bus'
import { RuntimeContext } from '../contracts'
import { RuntimeEngine } from '../orchestration/engine'
import { v4 as uuidv4 } from 'uuid'

type LegacyAuthorizationPayload = {
  type: RuntimeEventType
  actor?: string
  route?: string
  method?: string
  operation?: string
  capability?: string
  requiredCapabilities?: string[]
  grantedCapabilities?: string[]
  authorized: boolean
  denialReason?: string
  metadata?: Record<string, any>
}

type LegacyBusinessPayload = {
  type: RuntimeEventType
  operation: string
  domain?: string
  status?: 'started' | 'completed' | 'failed'
  duration?: number
  result?: any
  error?: string
  actor?: string
  metadata?: Record<string, any>
}

export class RuntimeEventPublisher {
  static async publish(
    type: RuntimeEventType,
    category: EventCategory,
    source: EventSource,
    data: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata,
    options?: EventPublishingOptions
  ): Promise<void> {
    const event = this.createEvent(type, category, source, data, context, metadata)
    await RuntimeEventBus.publish(event, options)
  }

  static async publishRuntimeEvent(
    type: RuntimeEventType,
    data: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    await this.publish(type, EventCategory.RUNTIME, EventSource.RUNTIME_ENGINE, data, context, metadata)
  }

  static async publishOperationalEvent(
    type: RuntimeEventType,
    data: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    await this.publish(type, EventCategory.OPERATIONAL, EventSource.APPLICATION_SERVICE, data, context, metadata)
  }

  static async publishAccountBalanceEvent(
    type: RuntimeEventType,
    data: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    await this.publish(type, EventCategory.ACCOUNT_BALANCE, EventSource.API_GATEWAY, data, context, metadata)
  }

  static async publishAuthorizationEvent(
    typeOrPayload: RuntimeEventType | LegacyAuthorizationPayload,
    data?: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    if (typeof typeOrPayload === 'object') {
      const payload = typeOrPayload
      await this.publish(
        payload.type,
        EventCategory.AUTHORIZATION,
        EventSource.SECURITY_AUDIT,
        {
          actor: payload.actor,
          route: payload.route,
          method: payload.method,
          operation: payload.operation,
          capability: payload.capability,
          requiredCapabilities: payload.requiredCapabilities,
          grantedCapabilities: payload.grantedCapabilities,
          authorized: payload.authorized,
          denialReason: payload.denialReason,
          metadata: payload.metadata
        },
        context,
        { ...metadata, priority: payload.authorized ? EventPriority.NORMAL : EventPriority.HIGH }
      )
      return
    }

    await this.publish(typeOrPayload, EventCategory.AUTHORIZATION, EventSource.SECURITY_AUDIT, data || {}, context, metadata)
  }

  static async publishBusinessEvent(
    typeOrPayload: RuntimeEventType | LegacyBusinessPayload,
    data?: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    if (typeof typeOrPayload === 'object') {
      const payload = typeOrPayload
      await this.publish(
        payload.type,
        EventCategory.OPERATIONAL,
        EventSource.APPLICATION_SERVICE,
        {
          operation: payload.operation,
          domain: payload.domain || 'runtime',
          status: payload.status,
          duration: payload.duration,
          result: payload.result,
          error: payload.error,
          actor: payload.actor,
          metadata: payload.metadata
        },
        context,
        metadata
      )
      return
    }

    await this.publish(typeOrPayload, EventCategory.OPERATIONAL, EventSource.APPLICATION_SERVICE, data || {}, context, metadata)
  }

  static async publishSystemEvent(type: RuntimeEventType, data: Record<string, any>, context?: Partial<RuntimeContext>, metadata?: EventMetadata): Promise<void> {
    await this.publish(type, EventCategory.RUNTIME, EventSource.RUNTIME_ENGINE, data, context, metadata)
  }

  static async publishSecurityEvent(
    typeOrPayload: RuntimeEventType | Record<string, any>,
    dataOrContext?: Record<string, any> | Partial<RuntimeContext>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): Promise<void> {
    if (typeof typeOrPayload === 'object') {
      await this.publish(RuntimeEventType.SECURITY_VIOLATION, EventCategory.SECURITY, EventSource.SECURITY_AUDIT, typeOrPayload, dataOrContext as Partial<RuntimeContext>, metadata)
      return
    }

    await this.publish(typeOrPayload, EventCategory.SECURITY, EventSource.SECURITY_AUDIT, (dataOrContext as Record<string, any>) || {}, context, metadata)
  }

  static async publishErrorEvent(type: RuntimeEventType, data: Record<string, any>, context?: Partial<RuntimeContext>, metadata?: EventMetadata): Promise<void> {
    await this.publish(type, EventCategory.ERROR, EventSource.APPLICATION_SERVICE, data, context, metadata)
  }

  private static createEvent(
    type: RuntimeEventType,
    category: EventCategory,
    source: EventSource,
    data: Record<string, any>,
    context?: Partial<RuntimeContext>,
    metadata?: EventMetadata
  ): RuntimeEvent {
    const currentContext = RuntimeEngine.getCurrentContext()

    const eventContext: RuntimeContext = {
      executionId: context?.executionId || currentContext?.executionId || `exec_${uuidv4()}`,
      correlationId: context?.correlationId || currentContext?.correlationId || `corr_${uuidv4()}`,
      timestamp: context?.timestamp || new Date(),
      actor: context?.actor || currentContext?.actor || null,
      route: context?.route || currentContext?.route || null,
      service: context?.service || currentContext?.service || 'runtime.events',
      domain: context?.domain || currentContext?.domain || 'runtime',
      ...context
    }

    return {
      id: uuidv4(),
      type,
      category,
      timestamp: new Date(),
      source,
      context: eventContext,
      data,
      metadata: {
        priority: EventPriority.NORMAL,
        version: '1.0',
        ...metadata
      }
    }
  }
}